import { IVeriNavPill } from "@directives/_veri/veriPills/veriPills.component";
import { IVeriTableHeader, IVeriTableRow } from "@directives/_veri/veriTable/veriTable.component";
import { IDateFilter } from "@interfaces/filters/IDateFilter";
import { DisplayService, IDialogOptionsJson } from "@services/display.service";
import { NavigationService } from "@services/navigation.service";
import { VeriDate } from "@services/veriDate.service";
import { TmsModuleNavigation } from "@tms/tms.navigation";
import { IPlannedRoute } from "@tms/_models/IPlannedRoute";
import { IPlannedRouteDetail, PlannedRouteService } from "@tms/_services/planned-route.service";
import { SchedulingService } from "@tms/_services/scheduling.service";
import "./scheduling.component.scss";

interface IComponentBindings {
}

export class SchedulingComponentController implements angular.IComponentController, IComponentBindings {

    plannedRoutes: IPlannedRouteDetail[] = [];

    tableRows: IVeriTableRow<IPlannedRoute>[] = [];
    tableHeaders: IVeriTableHeader[] = [];

    dateFilter: IDateFilter;
    selectedDate: Date;
    loading: boolean;

    navPills: IVeriNavPill<IPlannedRoute[]>[] = [];
    selectedNavIndex: number = 0;

    quickEditRecord = false;
    editMenuActive = false;
    menuY: number;
    menuX: number;
    routeToEdit: IPlannedRouteDetail;

    static $inject = ["$q", "$scope", "displayService", "veriDate", "plannedRouteService", "schedulingService", "navigationService"];

    constructor(
        private readonly $q: angular.IQService,
        private readonly $scope: angular.IScope,
        private readonly displayService: DisplayService,
        private readonly veriDate: VeriDate,
        private readonly plannedRouteService: PlannedRouteService,
        private readonly schedulingService: SchedulingService,
        private readonly navigationService: NavigationService
    ) {
        this.createNavPills();

        this.selectedDate = new Date();
        this.dateFilter = {
            start: this.veriDate.createMoment(this.selectedDate).startOf("d").toDate(),
            end: this.veriDate.createMoment(this.selectedDate).add(7, "d").startOf("d").toDate()
        }
    }

    $onInit() {
        this.tableHeaders = this.schedulingService.createHistoryTableHeaders();
    }

    createNewSchedule() {
        this.navigationService.go(TmsModuleNavigation.NewSchedule);
    }

    gotoImport() {
        this.navigationService.go(TmsModuleNavigation.ImportSchedule);
    }

    selectRoute(plannedRoute: IPlannedRoute, $event: MouseEvent) {
        this.editMenuActive = false;
        this.navigationService.go(TmsModuleNavigation.ScheduleView, { routeId: plannedRoute.Id, route: plannedRoute });
    }

    showEditMenu(data: IPlannedRouteDetail, $event: MouseEvent) {
        this.routeToEdit = data;
        this.editMenuActive = true;
        this.menuX = $event.clientX;
        this.menuY = $event.clientY;
    }

    showQuickEdit() {
        this.editMenuActive = false;
        const dialogOptions: IDialogOptionsJson = {
            title: this.routeToEdit.routeDisplayName,
            dialogHTML: `<md-input-container class="md-block">
                            <veri-autocomplete include-vehicles="true"
                                               include-drivers="true"
                                               custom-place-holder-text="No Vehicle/Driver Assigned"
                                               on-select="$ctrl.assetChanged($match); $ctrl.hide();"
                                               search-on-click="true">
                            </veri-autocomplete>
                        </md-input-container>`,
            dialogId: `routeDriverVehicleEditor`,
            customClass: `route-driver-vehicle-editor-dialog`,
            bindings: {
                assetChanged: ($match) => { this.assetChanged($match); }
            }
        };

        return this.displayService.showDialog(dialogOptions);
    }

    assetChanged(result) {
        if (result && result.source === "Vehicle") {
            this.routeToEdit.VehicleId = result?.object ? result.object.Id : null;
        }
        if (result && result.source === "Driver") {
            this.routeToEdit.DriverId = result?.object ? result.object.Id : null;
        }

        this.plannedRouteService.updatePlannedRoute(this.routeToEdit).then(() => {
            this.getData(true, true);
        })

    }

    onNavPillSelected(selectedNavPill: IVeriNavPill<IPlannedRoute[]>) {
        this.selectedNavIndex = this.navPills.indexOf(selectedNavPill);
        this.plannedRoutes = selectedNavPill.data;

        this.createTableRows();
    }

    onDateFilterChanged(filter: IDateFilter): void {
        this.dateFilter = filter;

        this.getData(true, true);
    }

    // ---

    private getData(showLoadingIndicator: boolean, invalidateCache = false): angular.IPromise<void> {
        if (this.loading) {
            return this.$q.resolve();
        }

        const start = this.veriDate.createMoment(this.dateFilter.start);
        const end = this.veriDate.createMoment(this.dateFilter.end);

        this.loading = showLoadingIndicator;

        return this.plannedRouteService
            .getPlannedRoutes(start, end, { mapVehicles: true, mapDrivers: true, onlyRoutesThatCanBeEdited: true, invalidateCache: invalidateCache })
            .then(plannedRoutes => {
                this.plannedRoutes = plannedRoutes;
                this.createTableRows();
            })
            .finally(() => {
                this.updateNavPills();
                this.loading = false;
            });
    }

    private createTableRows(): void {
        this.tableRows = this.schedulingService.createHistoryTableRows(this.plannedRoutes);

        this.$scope.$applyAsync();
    }

    private createNavPills() {
        this.navPills.push({ title: "All", count: 0, data: [], selected: true });
        this.navPills.push({ title: "Allocated", count: 0, data: [] });
        this.navPills.push({ title: "Unallocated", count: 0, data: [] });
    }

    private updateNavPills() {
        if (!this.navPills?.length) {
            return;
        }

        this.clearNavPillsData();

        this.navPills[0].data = this.plannedRoutes.filter(route => route); // All
        this.navPills[1].data = this.plannedRoutes.filter(route => route.VehicleId || route.DriverId); // Allocated
        this.navPills[2].data = this.plannedRoutes.filter(route => !route.VehicleId && !route.DriverId); // Unallocated

        this.recountNavPillTotals();
    }

    private clearNavPillsData(): void {
        this.navPills.forEach(label => {
            label.count = 0;
            label.data = [];
        });
    }

    private recountNavPillTotals(): void {
        this.navPills?.forEach(pill => pill.count = pill.data.length);
    }
}

export class SchedulingComponent implements angular.IComponentOptions {
    bindings: any;
    controller: any;
    template: string;

    constructor() {
        this.bindings = {};

        this.controller = SchedulingComponentController;
        this.template = require("./scheduling.component.html");
    }
}
